import styled, { css } from 'styled-components';

export const QueuePanel = styled.div`
  box-sizing: border-box;
  width: min(100%, 1180px);
  padding: 32px 36px 36px;
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};

  @media (max-width: 768px) {
    padding: 24px 18px 28px;
  }

  @media (max-width: 639px) {
    padding: 20px 12px 24px;
    border-radius: 12px;
  }
`;

export const QueueTableScroll = styled.div`
  width: 100%;
  overflow-x: auto;

  > div {
    min-width: 560px;
  }

  @media (max-width: 639px) {
    overflow: visible;

    > div {
      min-width: 0;
    }
  }
`;

export const ReviewPanel = styled.div`
  box-sizing: border-box;
  width: min(100%, 1180px);
  padding: 38px 48px 42px;
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};

  @media (max-width: 900px) {
    padding: 28px 20px 32px;
  }

  @media (max-width: 639px) {
    padding: 20px 12px 24px;
    border-radius: 12px;
  }
`;

export const RevisionSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 24px;
  margin-bottom: 28px;
  padding: 16px 18px;
  border-left: 4px solid ${(p) => p.theme.colors.primary500};
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.gray000};

  @media (max-width: 639px) {
    align-items: flex-start;
    margin-bottom: 22px;
    padding: 14px 12px;
  }
`;

export const LawyerName = styled.p`
  margin: 0;
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.md};
  font-weight: 700;
  overflow-wrap: anywhere;
`;

export const RevisionMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 22px;
  color: ${(p) => p.theme.colors.gray400};
  font-size: ${(p) => p.theme.fontSizes.sm};
`;

export const ReviewIntro = styled.p`
  margin: 0 0 28px;
  color: ${(p) => p.theme.colors.gray400};
  font-size: ${(p) => p.theme.fontSizes.base};
  line-height: 1.5;
`;

export const ReviewSection = styled.section`
  & + & {
    margin-top: 26px;
    padding-top: 26px;
    border-top: 1px solid ${(p) => p.theme.colors.gray100};
  }
`;

export const SectionTitle = styled.h2`
  margin: 0 0 18px;
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.base};
  font-weight: 700;
`;

const comparisonColumns = css`
  grid-template-columns: minmax(190px, 27%) repeat(2, minmax(0, 1fr));
  column-gap: 18px;
`;

export const ComparisonHeader = styled.div`
  display: grid;
  ${comparisonColumns};
  align-items: end;
  margin-bottom: 2px;
  padding: 0 12px 8px;
  color: ${(p) => p.theme.colors.gray400};
  font-size: ${(p) => p.theme.fontSizes.sm};
  font-weight: 700;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const FieldRow = styled.div<{ $changed: boolean }>`
  display: grid;
  ${comparisonColumns};
  align-items: stretch;
  padding: 14px 12px;
  border-top: 1px solid ${(p) => p.theme.colors.gray100};
  background: ${(p) => (p.$changed ? '#FAFCFF' : 'transparent')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 16px 0;
  }
`;

export const FieldLabel = styled.p`
  align-self: center;
  margin: 0;
  color: ${(p) => p.theme.colors.gray300};
  font-size: ${(p) => p.theme.fontSizes.base};
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

export const ChangedBadge = styled.span`
  display: inline-block;
  margin: 5px 0 0;
  padding: 2px 7px;
  border-radius: 999px;
  background: #edf5fe;
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.xs};
  font-weight: 700;
`;

export const VersionValue = styled.div<{ $changed: boolean; $proposed?: boolean }>`
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid ${(p) => (p.$changed && p.$proposed ? '#C7DFFB' : p.theme.colors.gray100)};
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => (p.$changed && p.$proposed ? '#EDF5FE' : p.theme.colors.light)};
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base};
  line-height: 1.4;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

export const MobileVersionLabel = styled.span`
  display: none;

  @media (max-width: 768px) {
    display: block;
    margin-bottom: 4px;
    color: ${(p) => p.theme.colors.gray300};
    font-size: ${(p) => p.theme.fontSizes.sm};
    font-weight: 700;
  }
`;

export const EmptyState = styled.p`
  margin: 0;
  color: ${(p) => p.theme.colors.gray300};
  font-size: ${(p) => p.theme.fontSizes.base};
`;

export const DecisionSection = styled.section`
  margin-top: 32px;
  padding-top: 28px;
  border-top: 1px solid ${(p) => p.theme.colors.gray100};
`;

export const ReasonLabel = styled.label`
  display: block;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base};
  font-weight: 700;
`;

export const ReasonHelp = styled.p`
  margin: 6px 0 0;
  color: ${(p) => p.theme.colors.gray300};
  font-size: ${(p) => p.theme.fontSizes.sm};
  line-height: 1.4;
`;

export const ReasonTextarea = styled.textarea<{ $invalid: boolean }>`
  box-sizing: border-box;
  width: 100%;
  min-height: 112px;
  margin-top: 10px;
  padding: 12px 14px;
  border: 1px solid ${(p) => (p.$invalid ? p.theme.colors.error : p.theme.colors.gray100)};
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.light};
  color: ${(p) => p.theme.colors.gray500};
  font-family: inherit;
  font-size: ${(p) => p.theme.fontSizes.base};
  line-height: 1.45;
  resize: vertical;

  &::placeholder {
    color: ${(p) => p.theme.colors.gray300};
  }

  &:focus {
    border-color: ${(p) => (p.$invalid ? p.theme.colors.error : p.theme.colors.primary300)};
    outline: 2px solid ${(p) => (p.$invalid ? '#FFE8E8' : '#EDF5FE')};
    outline-offset: 1px;
  }
`;

export const FieldError = styled.p`
  margin: 6px 0 0;
  color: ${(p) => p.theme.colors.error};
  font-size: ${(p) => p.theme.fontSizes.sm};
`;

export const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;

  @media (max-width: 640px) {
    flex-direction: column;

    > button {
      width: 100%;
    }
  }
`;
